
/**
 * Utility functions for admin logging
 */

import { supabase } from '../supabase';

// Cache for admin status to avoid excessive database calls
const adminStatusCache: Map<string, { isAdmin: boolean, timestamp: number }> = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Check if current user is an admin
export async function isCurrentUserAdmin(): Promise<boolean> {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    
    const userId = user.id;
    
    // Check cache first
    const cached = adminStatusCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.isAdmin;
    }
    
    // If not in cache or cache expired, check the database
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .single();
    
    const isAdmin = !!data && !error;
    
    // Update the cache
    adminStatusCache.set(userId, { isAdmin, timestamp: Date.now() });
    
    return isAdmin;
  } catch (e) {
    console.error('Error checking admin status:', e);
    return false;
  }
}

// Separate cache for whether we're in development mode
let isDevelopment: boolean | null = null;

// Helper function to check if we're running in development mode
async function checkIsDevelopmentMode(): Promise<boolean> {
  if (isDevelopment === null) {
    isDevelopment = process.env.NODE_ENV === 'development';
  }
  return isDevelopment;
}

// Log message for admin purposes with timestamp
export async function adminLog(...args: any[]): Promise<void> {
  const isAdmin = await isCurrentUserAdmin();
  const inDevelopment = await checkIsDevelopmentMode();
  
  if (isAdmin || inDevelopment) {
    const timestamp = new Date().toISOString();
    console.log(`[ADMIN ${timestamp}]`, ...args);
  }
}

// Log error for admin purposes with timestamp
export async function adminError(...args: any[]): Promise<void> {
  const isAdmin = await isCurrentUserAdmin();
  const inDevelopment = await checkIsDevelopmentMode();
  
  if (isAdmin || inDevelopment) {
    const timestamp = new Date().toISOString();
    console.error(`[ADMIN ERROR ${timestamp}]`, ...args);
  }
}

// Log warning for admin purposes with timestamp
export async function adminWarn(...args: any[]): Promise<void> {
  const isAdmin = await isCurrentUserAdmin();
  const inDevelopment = await checkIsDevelopmentMode();
  
  if (isAdmin || inDevelopment) {
    const timestamp = new Date().toISOString();
    console.warn(`[ADMIN WARN ${timestamp}]`, ...args);
  }
}

// Log info for admin purposes with timestamp
export async function adminInfo(...args: any[]): Promise<void> {
  const isAdmin = await isCurrentUserAdmin();
  const inDevelopment = await checkIsDevelopmentMode();
  
  if (isAdmin || inDevelopment) {
    const timestamp = new Date().toISOString();
    console.info(`[ADMIN INFO ${timestamp}]`, ...args);
  }
}
